import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import './theme.css';
import posthog from 'posthog-js';
import Box from '@mui/material/Box';
import Navbar from './components/website/Navbar';
import ThemeToggle from './components/common/ThemeToggle';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MortyThemeProvider } from './contexts/ColorModeContext';
import { refreshFavicons } from './utils/favicon';
import { isRouteChunkLoadError } from './routeChunkErrors';

const AppRoutes = React.lazy(() => import('./AppRoutes'));

function RouteLoadingFallback() {
  return (
    <div className="route-status-screen" role="status" aria-live="polite">
      <div className="route-status-card">
        <div className="route-status-spinner" />
        <h1>Loading Morty</h1>
        <p>Opening your workspace...</p>
      </div>
    </div>
  );
}

class RouteChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Failed to render routed app shell:', error, errorInfo);
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoToLogin = () => {
    window.location.assign('/login');
  };

  render() {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }

    const isChunkError = isRouteChunkLoadError(error);

    return (
      <div className="route-status-screen" role="alert">
        <div className="route-status-card route-status-card--error">
          <h1>{isChunkError ? 'Morty needs a refresh' : 'Something went wrong'}</h1>
          <p>
            {isChunkError
              ? 'A new version was deployed while this tab still had older app files cached. Refresh to load the current app.'
              : 'The app hit an error before this page could open.'}
          </p>
          <div className="route-status-actions">
            <button type="button" className="route-status-primary-button" onClick={this.handleRefresh}>
              Refresh
            </button>
            <button type="button" className="route-status-secondary-button" onClick={this.handleGoToLogin}>
              Go to login
            </button>
          </div>
        </div>
      </div>
    );
  }
}

function AppContent() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  React.useEffect(() => {
    refreshFavicons();
    // Track pageview on route change
    posthog.capture('$pageview');
  }, [location.pathname, location.search]);

  const showNavbar = isAuthenticated &&
    location.pathname !== '/upload' &&
    location.pathname !== '/upload-builder' &&
    location.pathname !== '/borrower-docs' &&
    location.pathname !== '/login' &&
    location.pathname !== '/magic_log_in' &&
    !location.pathname.startsWith('/upload/') &&
    !location.pathname.startsWith('/contractor-approval/') &&
    !location.pathname.startsWith('/borrower-reimbursement/') &&
    !location.pathname.startsWith('/paid-in-full/') &&
    !location.pathname.startsWith('/bank-wiring-instructions/') &&
    !location.pathname.startsWith('/builder-experience/') &&
    !location.pathname.startsWith('/loan-disbursement/') &&
    !location.pathname.startsWith('/pest-control/') &&
    !location.pathname.startsWith('/pest-control-service-record/') &&
    !location.pathname.includes('/split-review/');

  return (
    <>
      {process.env.REACT_APP_ENVIRONMENT === 'staging' && (
        <div className="staging-environment-badge">STAGING · Test data</div>
      )}
      {showNavbar && <Navbar />}
      {!showNavbar && (
        <Box className="public-theme-toggle">
          <ThemeToggle />
        </Box>
      )}
      <div className={showNavbar ? "App" : "App no-navbar"}>
        <RouteChunkErrorBoundary>
          <React.Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/*" element={<AppRoutes />} />
            </Routes>
          </React.Suspense>
        </RouteChunkErrorBoundary>
      </div>
    </>
  );
}

function App() {
  return (
    <MortyThemeProvider>
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </MortyThemeProvider>
  );
}

export default App;
